<template>
  <form @submit.prevent="submit">
    <div class="pb-3">{{ $t("global.advanced-auto-scale-service-interruption-note") }}</div>
    <CardTabledList>
      <tr>
        <td>
          <label for="advanced-auto-scale-additional-containers">
            {{ $t("global.containers") }}
          </label>
        </td>
        <td>
          <div class="d-flex justify-content-end">
            <input
              id="advanced-auto-scale-additional-containers"
              v-model="fields.scale"
              type="number"
              min="0"
              max="30"
              :disabled="isSubmitting"
              class="form-control containers-input"
            />
          </div>
        </td>
      </tr>
      <tr>
        <td>{{ $t("global.workers") }}</td>
        <td>{{ getWorkers() }}</td>
      </tr>
      <tr>
        <td>{{ $t("global.amount") }}</td>
        <td>{{ getAmount() }}</td>
      </tr>
    </CardTabledList>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-update-site-advanced-auto-scale-containers-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="update-site-advanced-auto-scale-containers-button"
      >
        {{ $t("global.update") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import CardTabledList from "@/components/display/card-sub-components/CardTabledList";
import { moneyFormat } from "leatherman-js/utils/formatters/money";
import store from "@/store";

export default {
  name: "SiteUpdateAdvancedAutoScaleContainersForm",
  props: {
    advancedAutoScale: {
      type: Object,
      required: true,
    },
    plan: {
      type: Object,
      required: true,
    },
    site: {
      type: Object,
      required: true,
    },
  },
  components: {
    Btn,
    CardTabledList,
  },
  setup(props, { emit, root }) {
    const isSubmitting = ref(false);

    const getAdditionalContainers = () =>
      props.advancedAutoScale.addon.quantity.containers -
      props.advancedAutoScale.addon.baseQuantity.containers;

    const fields = reactive({ scale: getAdditionalContainers() });

    const cancel = () => emit("close");

    const isSubmittable = () =>
      !isSubmitting.value && Number(fields.scale) !== getAdditionalContainers();

    const getAmount = () =>
      moneyFormat(Number(fields.scale) * props.advancedAutoScale.usage.additionalHoursPrice * 24);

    const getContainerOptions = (maxContainers) => {
      const createOptionsObject = (value) => ({ value: value, display: value.toString() });
      let options = [];
      for (let i = 0; i <= maxContainers; i++) {
        options.push(createOptionsObject(i));
      }
      return options;
    };

    const getWorkers = () =>
      Number(fields.scale) * props.advancedAutoScale.addon.quantity.workersPerContainer;

    const submit = () => {
      isSubmitting.value = true;
      const name = props.plan.isTShirt ? props.plan.nickname : props.site.domain;
      store
        .dispatch("sites/updateAdvancedAutoScale", {
          id: props.site.id,
          data: {
            scale: fields.scale,
          },
        })
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "info",
            message: root.$t("toasts.advanced-auto-scale-updating-message", { name }),
          });
          emit("close");
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: root.$t("toasts.advanced-auto-scale-update-error-message", { name }),
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    };

    return {
      cancel,
      fields,
      isSubmittable,
      isSubmitting,
      getAdditionalContainers,
      getAmount,
      getContainerOptions,
      getWorkers,
      submit,
    };
  },
};
</script>

<style lang="scss" scoped>
.containers-input {
  width: auto;
}
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
